import type {ReactElement} from 'react';
import {useCallback, useState} from 'react';
import {Text, TextSize} from '@Components/text';
import {ClickableDiv} from '@Components/clickable-div';
import {SkeletonBrick} from '@Components/skeleton-brick';
import type {QRDesignTile} from '@Components/qr-code-generator/qr-code-generator.types';
import {getDesignTileAspectRatio} from '@Components/qr-code-generator/qr-code-generator.library';
import {useIsCompactQRTool} from '@Components/qr-code-generator/qr-code-generator-hooks';
import styles from './qr-suggested-templates.module.scss';
import type {QRSuggestedTemplatesProps} from './qr-suggested-templates.types';
import {QR_TEMPLATE_GALLERY_PATH} from './qr-suggested-templates.types';

export function QRSuggestedTemplates({tiles, blockClasses = '', onTemplateSelect, onBrowseAllClick}: QRSuggestedTemplatesProps): ReactElement | null {
  const [openingDesignHashedId, setOpeningDesignHashedId] = useState<string | null>(null);
  const [loadedDesignHashedIds, setLoadedDesignHashedIds] = useState<ReadonlySet<string>>(new Set<string>());
  const isCompact = useIsCompactQRTool();

  const onThumbnailSettled = useCallback((designHashedId: string): void => {
    setLoadedDesignHashedIds((alreadyLoaded: ReadonlySet<string>): ReadonlySet<string> => {
      if (alreadyLoaded.has(designHashedId)) {
        return alreadyLoaded;
      }
      return new Set<string>([...alreadyLoaded, designHashedId]);
    });
  }, []);

  if (tiles.length === 0) {
    return null;
  }

  const renderTemplate = (tile: QRDesignTile): ReactElement => {
    const aspectRatio = getDesignTileAspectRatio(tile);
    const hasLoaded = loadedDesignHashedIds.has(tile.designHashedId);

    return (
      <ClickableDiv
        key={tile.designHashedId}
        className={styles.qrSuggestedTemplate}
        style={aspectRatio === null ? undefined : {aspectRatio}}
        aria-label={tile.name}
        aria-busy={openingDesignHashedId === tile.designHashedId}
        onClick={(): void => {
          setOpeningDesignHashedId(tile.designHashedId);
          void onTemplateSelect(tile.designHashedId).finally(() => {
            setOpeningDesignHashedId(null);
          });
        }}
      >
        {hasLoaded ? null : <SkeletonBrick className={styles.qrSuggestedTemplateSkeleton} />}
        <img
          className={`${styles.qrSuggestedTemplateThumbnail} ${hasLoaded ? '' : styles.qrSuggestedTemplateThumbnailLoading}`}
          src={tile.thumbnailUrl}
          alt={tile.name}
          width={tile.width}
          height={tile.height}
          loading="lazy"
          decoding="async"
          onLoad={(): void => {
            onThumbnailSettled(tile.designHashedId);
          }}
          onError={(): void => {
            onThumbnailSettled(tile.designHashedId);
          }}
        />
      </ClickableDiv>
    );
  };

  return (
    <div className={`${styles.qrSuggestedTemplates} ${blockClasses}`}>
      <div className={styles.qrSuggestedTemplatesHeader}>
        <Text
          val={window.i18next.t('pmwjs_qr_generator_suggested_templates_heading')}
          size={isCompact ? TextSize.MD : TextSize.LG}
          bold
          className={styles.qrSuggestedTemplatesHeading}
        />
        <a className={styles.qrSuggestedTemplatesCta} href={window.PMW.util.site_url(QR_TEMPLATE_GALLERY_PATH)} onClick={onBrowseAllClick}>
          {window.i18next.t('pmwjs_qr_generator_browse_all_templates')}
        </a>
      </div>
      <div className={styles.qrSuggestedTemplatesGrid}>{tiles.map(renderTemplate)}</div>
    </div>
  );
}
